import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Quote, Star } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { testimonials } from '../data/testimonials';
const AUTOPLAY_MS = 6000;
export function TestimonialsCarousel() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const goTo = useCallback((next: number, dir: number) => {
    setDirection(dir);
    setIndex((next + testimonials.length) % testimonials.length);
  }, []);
  const next = useCallback(() => goTo(index + 1, 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1, -1), [goTo, index]);
  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(next, AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, [isPaused, next]);
  const current = testimonials[index];
  return <div className="relative max-w-3xl mx-auto" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} role="region" aria-roledescription="carousel" aria-label="Testimonios de profesoras" data-id="element-258" data-mp="1flhzvhewh90q">
      <div className="flex items-center gap-3 md:gap-5" data-id="element-259" data-mp="2rb4lgflx5p8v">
        <button type="button" onClick={prev} aria-label="Testimonio anterior" className="hidden sm:flex flex-shrink-0 items-center justify-center w-11 h-11 rounded-full bg-white border border-brand-pastelpink text-brand-pink shadow-sm hover:bg-brand-pink hover:text-white hover:border-brand-pink transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-pink focus-visible:ring-offset-2" data-id="element-260" data-mp="2rdqrubx2pypi">
          <ChevronLeft size={22} data-id="element-261" data-mp="3f0pang8o1iql" />
        </button>

        <div className="relative flex-1 overflow-hidden" data-id="element-262" data-mp="3k0jvp35rfe3q">
          <AnimatePresence mode="wait" initial={false} custom={direction} data-id="element-263" data-mp="3k24qmtvzkz24">
            <motion.blockquote key={index} custom={direction} initial={{
            opacity: 0,
            x: direction * 40
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: direction * -40
          }} transition={{
            duration: 0.28,
            ease: [0.23, 1, 0.32, 1]
          }} className="bg-white rounded-3xl shadow-soft border border-brand-pastelpink px-6 py-8 md:px-10 md:py-10 text-center" data-id="element-264" data-mp="3p06d7osmfgic">
              <Quote size={32} className="mx-auto text-brand-pastelpink mb-4" aria-hidden="true" data-id="element-265" data-mp="27ltmaujzapcg" />

              <div className="flex justify-center text-yellow-400 mb-5" aria-label="5 de 5 estrellas" data-id="element-266" data-mp="2hm2mskl06wxn">
                {[...Array(5)].map((_, i) => <Star key={i} size={18} fill="currentColor" data-id="element-267" data-mp="n702r2eq4724" data-mpi={i} />)}
              </div>

              <p className="text-brand-black text-base md:text-lg leading-relaxed italic mb-6" data-id="element-268" data-mp="i9i3y4uf787u">
                “{current.text}”
              </p>

              <footer data-id="element-269" data-mp="2ty4zcx9bxvwo">
                <p className="font-bold text-brand-black" data-id="element-270" data-mp="2yuvgzp0s8zk7">{current.name}</p>
                <p className="text-xs uppercase tracking-widest text-brand-pink mt-1" data-id="element-271" data-mp="2yx7yy26jj9x9">
                  {current.role}
                </p>
              </footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>

        <button type="button" onClick={next} aria-label="Testimonio siguiente" className="hidden sm:flex flex-shrink-0 items-center justify-center w-11 h-11 rounded-full bg-white border border-brand-pastelpink text-brand-pink shadow-sm hover:bg-brand-pink hover:text-white hover:border-brand-pink transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-pink focus-visible:ring-offset-2" data-id="element-272" data-mp="2b5163d00xl2l">
          <ChevronRight size={22} data-id="element-273" data-mp="4f4dahzwjrbj" />
        </button>
      </div>

      {/* Mobile arrows + dots */}
      <div className="flex items-center justify-center gap-4 mt-6" data-id="element-274" data-mp="20i93juba01jz">
        <button type="button" onClick={prev} aria-label="Testimonio anterior" className="sm:hidden flex items-center justify-center w-10 h-10 rounded-full bg-white border border-brand-pastelpink text-brand-pink shadow-sm" data-id="element-275" data-mp="20hg5dhqdukdg">
          <ChevronLeft size={20} data-id="element-276" data-mp="1mhevjcs542lu" />
        </button>

        <div className="flex items-center gap-2" data-id="element-277" data-mp="1mkudcnd77pqy">
          {testimonials.map((testimonial, i) => <button key={testimonial.name} type="button" onClick={() => goTo(i, i > index ? 1 : -1)} aria-label={`Ver testimonio de ${testimonial.name}`} aria-current={i === index} className={`h-2 rounded-full transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-pink focus-visible:ring-offset-2 ${i === index ? 'w-7 bg-brand-pink' : 'w-2 bg-brand-pastelpink hover:bg-brand-pink/60'}`} data-id="element-278" data-mp="1csojt97at09n" data-mpi={i} />)}
        </div>

        <button type="button" onClick={next} aria-label="Testimonio siguiente" className="sm:hidden flex items-center justify-center w-10 h-10 rounded-full bg-white border border-brand-pastelpink text-brand-pink shadow-sm" data-id="element-279" data-mp="oofhwxt8lr47">
          <ChevronRight size={20} data-id="element-280" data-mp="yksafsd1pyxd" />
        </button>
      </div>
    </div>;
}